// Thin wrapper around the channels service's read side of the policy
// API (see services/channels/src/channels_service/routers/policies.py).
// Everything a draft can mutate (routers/drafts.py) lives in
// draftsApi.js instead - this module only ever reads promoted
// policies/revisions/rules, since routers/policies.py itself is
// read-only with respect to those.
//
// Same as draftsApi.js: the frontend and the reverse proxy always share
// one origin (see services/proxy), so a relative path is all we need
// here - no base URL or CORS configuration required.
const API_BASE = '/api';

export class PoliciesError extends Error {
  constructor(message, { code, status } = {}) {
    super(message);
    this.name = 'PoliciesError';
    this.code = code;
    this.status = status;
  }
}

/**
 * Shared fetch + error-shape handling for every call in this module -
 * same JSON-body/error_description convention as draftsApi.js's
 * `request`.
 */
async function request(accessToken, path, { method = 'GET', body } = {}) {
  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${accessToken}`,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (err) {
    throw new PoliciesError(
      'Could not reach the server. Check your connection and try again.',
      { code: 'network_error' },
    );
  }

  let parsed = null;
  try {
    parsed = await response.json();
  } catch (err) {
    // Non-JSON response body (e.g. a 204); fall through with parsed === null.
  }

  if (!response.ok) {
    const message = parsed?.error_description ?? 'Something went wrong. Please try again.';
    throw new PoliciesError(message, { code: parsed?.error, status: response.status });
  }

  return parsed;
}

/**
 * Calls GET /api/policies. Own policies plus every public one,
 * keyset-paginated (see `Page` in schemas.py) - `?owner=` and `?q=`
 * narrow it further, same as the backend does.
 *
 * Resolves with `{ items: PolicyOut[], nextCursor: string|null }` on
 * success - pass `nextCursor` back in as `cursor` to fetch the next
 * page. Rejects with a PoliciesError otherwise.
 */
export async function listPolicies(accessToken, { owner, q, cursor, limit = 20 } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (owner) params.set('owner', owner);
  if (q) params.set('q', q);
  if (cursor) params.set('cursor', cursor);
  const body = await request(accessToken, `/policies?${params}`);
  return {
    items: Array.isArray(body?.items) ? body.items : [],
    nextCursor: body?.next_cursor ?? null,
  };
}

/**
 * Calls GET /api/policies/{owner}/{name}.
 *
 * Resolves with the `PolicyOut` on success. Rejects with a
 * PoliciesError otherwise - notably 404 (`not_found`), which is also
 * what a private policy the caller doesn't own looks like (visibility
 * is never leaked - see `ensure_policy_visible` in policies.py).
 */
export async function getPolicy(accessToken, owner, name) {
  return request(accessToken, `/policies/${owner}/${name}`);
}

/**
 * Calls GET /api/policies/{owner}/{name}/revisions.
 *
 * Resolves with the policy's revisions (`RevisionOut[]`, newest first,
 * no rules - see `get_revision` for those) on success. The first entry
 * is always the one currently in effect. Rejects with a PoliciesError
 * otherwise (404 `not_found`).
 */
export async function listRevisions(accessToken, owner, name, { limit = 20 } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  const body = await request(accessToken, `/policies/${owner}/${name}/revisions?${params}`);
  return Array.isArray(body) ? body : [];
}

/**
 * Calls GET /api/policies/{owner}/{name}/revisions/{rev}.
 *
 * Resolves with the `RevisionDetail` (revision metadata + its full,
 * ordinal-ordered rule list) on success. Rejects with a PoliciesError
 * otherwise (404 `not_found`).
 */
export async function getRevision(accessToken, owner, name, rev) {
  return request(accessToken, `/policies/${owner}/${name}/revisions/${rev}`);
}
